/**
 * PasskeyRegisterModal — 디자인 702-2647 적용 통합 모달.
 *
 * mode='new'   : 2-phase (#1329 클라 보관 모델)
 *   authenticating: open 즉시 beginPasskeyRegistration(ceremony 만, 서버 등록 없음)
 *   naming        : ceremony 결과를 메모리에 보관한 채 이름 입력 → [저장] 시
 *                   completePasskeyRegistration 으로 서버 등록 1회 → onRegistered
 *   - 확정 전엔 서버에 credential 이 없어 이탈/취소 = 무흔적 (목록 미노출)
 *   - challenge 는 Redis 5분 TTL — 초과 시 830 → [다시 인증] 으로 새 ceremony
 * mode='rename': patchCredentialLabel(credentialId, label) → onRenamed
 *
 * 디자인 의도(702-2647)에 따라 PRF preflight / OS별 안내 / multi-device 안내 등은 제거.
 * 호환성 안내는 OS 네이티브 다이얼로그 / 등록 실패 메시지로 위임.
 */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Input, Modal, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  PasskeyApiError,
  beginPasskeyRegistration,
  completePasskeyRegistration,
  patchCredentialLabel,
} from 'services/passkey'
import { autoDetectDeviceLabel } from 'services/passkey/deviceLabel'
import { errorBase } from 'utils/ui'
import classNames from 'classnames'

import exclamationCircleIcon from '@/assets/images/exclamation-circle-black-16.svg'
import exclamationCircleRed22 from '@/assets/images/exclamation-circle-red-22.svg'
import styles from './style.module.scss'

const MAX_LABEL_LENGTH = 100

// default 콜백 prop 은 렌더마다 새 함수 인스턴스가 생기지 않도록 모듈 레벨 고정 참조 사용
const NOOP = () => {}

// 사용자가 OS 패스키 프롬프트를 취소/중단했는지 — #1330: 취소 시 인라인 에러 없이 모달을 닫는다.
function isUserCancelledError(err) {
  const name = err?.name || ''
  // String() 래핑: err.message 가 비문자열(숫자/객체)일 때 .toLowerCase() 크래시 방지 (gemini #1331)
  const msg = String(err?.message || '').toLowerCase()
  return (
    name === 'NotAllowedError' ||
    name === 'TimeoutError' ||
    name === 'AbortError' ||
    msg.includes('timed out') ||
    msg.includes('not allowed') ||
    msg.includes('aborted')
  )
}

function mapWebAuthnError(err, t) {
  const name = err?.name || ''
  if (isUserCancelledError(err)) return t('passkey.error.userCancelled')
  switch (name) {
    case 'InvalidStateError':
      return t('passkey.error.alreadyRegistered')
    case 'NotSupportedError':
      return t('passkey.error.notSupported')
    case 'SecurityError':
      return t('passkey.error.securityError')
    case 'ConstraintError':
      return t('passkey.error.constraintError')
    default:
      return t('passkey.error.generic')
  }
}

const PasskeyRegisterModal = ({
  open = false,
  mode = 'new',
  umk = null,
  credentialId = null,
  initialLabel = '',
  onClose = NOOP,
  onRegistered = NOOP,
  onRenamed = NOOP,
}) => {
  const { t } = useTranslation()
  const [label, setLabel] = useState('')
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  // #1329: mode='new' 2-phase. rename 은 항상 'naming'(이름 UI 재사용).
  const [phase, setPhase] = useState('naming')
  const [ceremonyResult, setCeremonyResult] = useState(null)
  // 830/831(challenge 만료/불일치) 수신 플래그 — [저장] 을 [다시 인증] 으로 전환
  const [sessionExpired, setSessionExpired] = useState(false)

  const runCeremony = async () => {
    // UMK 세션 만료 등 — ceremony 시작 전 가드 (진입점들이 이미 막지만 이중 방어, #1319)
    if (!umk) {
      setError(t('passkey.error.generic'))
      return
    }
    setProcessing(true)
    setError(null)
    setSessionExpired(false)
    try {
      const result = await beginPasskeyRegistration()
      setCeremonyResult(result)
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
      // ceremony 가 확정한 attachment 로 정확한 초기 라벨 (cross-platform 키 구분)
      setLabel(autoDetectDeviceLabel(ua, result.authenticatorAttachment))
      setPhase('naming')
    } catch (err) {
      if (isUserCancelledError(err)) {
        // #1330: 사용자가 OS 패스키 프롬프트를 취소/중단 → 등록 의사 철회로 보고
        // 인라인 에러("취소되었어요…") 없이 모달을 조용히 닫는다.
        onClose?.()
        return
      }
      if (err?.name === 'InvalidStateError') {
        // 이미 (완료) 등록된 인증기 — 인라인 알림이 아닌 별도 팝업 (Figma 702-2627).
        // 클라 보관 모델에선 미확정 credential 이 excludeCredentials 에 없으므로
        // 이 분기는 "이름까지 완료된 같은 인증기 재등록" 시에만 발생.
        errorBase({
          wrapClassName: classNames('om-popup-error-1', 'om-popup-passkey-already'),
          title: t('passkey.error.alreadyRegisteredTitle'),
          content: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {t('passkey.error.alreadyRegisteredBody')}
            </span>
          ),
          icon: <img src={exclamationCircleRed22} alt="error" />,
          onOk: () => onClose?.(),
        })
      } else if (err instanceof PasskeyApiError && err.code !== 0) {
        // register-options 단계 백엔드 에러
        setError(t([`workspace.error.${err.code}`, 'passkey.error.generic']))
      } else {
        // 미지원·보안오류 등 기술적 실패 — 왜 안 되는지 안내 + [다시 시도]
        setError(mapWebAuthnError(err, t))
      }
    } finally {
      setProcessing(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setProcessing(false)
    setError(null)
    setSessionExpired(false)
    setCeremonyResult(null)
    if (mode === 'rename') {
      setLabel(initialLabel || '')
      setPhase('naming')
    } else {
      // #1329: 인증 먼저 — open 직후(등록 버튼 클릭의 user activation 윈도 내) ceremony 시작
      setLabel('')
      setPhase('authenticating')
      runCeremony()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialLabel])

  const trimmed = label.trim()
  const unchanged = mode === 'rename' && trimmed === (initialLabel || '').trim()
  const invalid = trimmed.length === 0 || trimmed.length > MAX_LABEL_LENGTH
  const saveDisabled = processing || invalid || unchanged

  const handleSave = async () => {
    if (saveDisabled) return
    setProcessing(true)
    setError(null)
    try {
      if (mode === 'rename') {
        await patchCredentialLabel(credentialId, trimmed)
        // #1319 ③ + gemini 리뷰 #1323 R3: 서버 변경 성공 후의 콜백(목록 갱신) 실패를
        // '이름 변경 실패'로 오인시키지 않는다 — 안내는 warn, 모달은 정상 닫기.
        try {
          await onRenamed?.(credentialId, trimmed)
        } catch (postErr) {
          console.warn('[passkey] post-rename refresh failed:', postErr)
        }
        onClose?.()
      } else {
        // gemini 리뷰 #1330: naming 진입은 ceremony 성공 후뿐이지만, 예기치 못한 상태
        // 전이로 ceremonyResult 가 없으면 TypeError 크래시 대신 catch 의 generic 처리로.
        if (!ceremonyResult) {
          throw new Error('ceremony result missing')
        }
        const result = await completePasskeyRegistration({
          challengeId: ceremonyResult.challengeId,
          credResponse: ceremonyResult.credResponse,
          deviceLabel: trimmed,
        })
        // gemini 리뷰 #1323 R4: 등록 성공 후의 콜백 실패가 '등록 실패'로 표시되면 사용자가
        // 재등록을 시도(840 혼란)할 수 있다 — R3 과 동일하게 격리.
        try {
          await onRegistered?.(result)
        } catch (postErr) {
          console.warn('[passkey] post-registration callback failed:', postErr)
        }
        onClose?.()
      }
    } catch (err) {
      if (err instanceof PasskeyApiError && (err.code === 830 || err.code === 831)) {
        // #1329: 이름 입력이 challenge TTL(5분) 을 넘김 — 재인증 유도
        setSessionExpired(true)
        setError(t('workspace.error.830'))
      } else if (err instanceof PasskeyApiError && err.code === 840) {
        setError(t('workspace.error.840'))
      } else if (err instanceof PasskeyApiError && err.code !== 0) {
        // 백엔드 영문 메시지를 직접 노출하지 않고 workspace.error.{code} 키로 번역.
        // 키 없으면 generic 으로 fallback.
        setError(t([`workspace.error.${err.code}`, 'passkey.error.generic']))
      } else {
        setError(t('passkey.error.generic'))
      }
    } finally {
      setProcessing(false)
    }
  }

  // 830/831 후 [다시 인증]: 클릭이 user gesture — 새 challenge 로 ceremony 재시작
  const handleReauth = () => {
    setPhase('authenticating')
    setCeremonyResult(null)
    runCeremony()
  }

  const isAuthenticating = mode !== 'rename' && phase === 'authenticating'

  return (
    <Modal
      open={open}
      onCancel={processing ? undefined : onClose}
      destroyOnClose
      width={480}
      className={styles.modalRoot}
      closable={false}
      maskClosable={!processing}
      footer={
        <>
          <Button className={styles.cancelBtn} onClick={onClose} disabled={processing}>
            {t('common.button.cancel', 'Cancel')}
          </Button>
          {isAuthenticating ? (
            // ceremony 실패 시에만 노출 — 진행 중에는 OS 다이얼로그가 주체
            error && (
              <Button
                className={classNames('type8', 'h38', 'w100')}
                type="primary"
                loading={processing}
                onClick={runCeremony}
              >
                {t('passkey.retry', 'Retry')}
              </Button>
            )
          ) : (
            <Button
              className={classNames('type8', 'h38', 'w100')}
              type="primary"
              loading={processing}
              onClick={sessionExpired ? handleReauth : handleSave}
              disabled={sessionExpired ? processing : saveDisabled}
            >
              {sessionExpired ? t('passkey.retry', 'Retry') : t('common.button.save', 'Save')}
            </Button>
          )}
        </>
      }
    >
      <div className={styles.content}>
        <img src={exclamationCircleIcon} alt="" className={styles.headerIcon} />
        <div className={styles.right}>
          <p className={styles.title}>
            {isAuthenticating ? t('passkey.authTitle') : t('passkey.nameTitle')}
          </p>
          <div className={styles.container}>
            {isAuthenticating ? (
              <>
                <p className={styles.desc}>
                  {!error && <Spin size="small" style={{ marginRight: 8 }} />}
                  {t('passkey.authInProgress')}
                </p>
                {error && <p className={styles.errorText}>{error}</p>}
              </>
            ) : (
              <>
                <p className={styles.desc}>{t('passkey.nameDesc')}</p>
                <Input
                  id="passkey-name-input"
                  className={styles.nameInput}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('passkey.namePlaceholder')}
                  maxLength={MAX_LABEL_LENGTH}
                  disabled={processing}
                  // G2: 830 후 Enter 가 만료된 challenge 로 재시도하는 루프 방지
                  onPressEnter={sessionExpired ? undefined : handleSave}
                />
                {error && <p className={styles.errorText}>{error}</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

PasskeyRegisterModal.propTypes = {
  open: PropTypes.bool,
  mode: PropTypes.oneOf(['new', 'rename']),
  umk: PropTypes.string,
  credentialId: PropTypes.string,
  initialLabel: PropTypes.string,
  onClose: PropTypes.func,
  onRegistered: PropTypes.func,
  onRenamed: PropTypes.func,
}

export default PasskeyRegisterModal
